import { create } from 'zustand';

// ── Store ────────────────────────────────────────────────────────────

/**
 * Tracks whether the login session has died, so the app can bail out once instead of
 * retrying forever.
 *
 * This exists because the signal can only come from the HTTP side. A WebSocket handshake
 * rejected with 401 closes as code 1006 with no reason — indistinguishable from a dropped
 * connection — so the socket layer can never work out on its own that it should stop. A
 * REST 401 is the only place the truth is visible, and every socket subscribes here.
 *
 * Zustand rather than context because `utils/websocket.ts` is constructed from non-React
 * code (xterm effects, the api-models modules) and needs `.getState()` / `.subscribe()`.
 */
interface SessionState {
  /** True once /user/me has succeeded. Gates `markExpired` — see below. */
  authenticated: boolean;
  expired: boolean;

  markAuthenticated: () => void;
  markExpired: () => void;
  clearExpired: () => void;
}

export const useSessionStore = create<SessionState>((set, get) => ({
  authenticated: false,
  expired: false,

  markAuthenticated: () => {
    if (!get().authenticated) set({ authenticated: true });
  },

  markExpired: () => {
    const { authenticated, expired } = get();
    // Two guards. Without `authenticated`, the login page's own expected 401 would latch
    // this and pause SWR for a user who was never signed in. Without the `expired` check,
    // the ~6 simultaneous 401s that arrive when a session dies would each re-render the
    // whole tree.
    if (!authenticated || expired) return;
    set({ expired: true });
  },

  clearExpired: () => {
    if (get().expired) set({ expired: false });
  },
}));

// ── Non-React helpers ────────────────────────────────────────────────
// For callers outside the React tree (fetch client, WebSocketConnection).

export function markSessionExpired(): void {
  useSessionStore.getState().markExpired();
}

export function isSessionExpired(): boolean {
  return useSessionStore.getState().expired;
}
